import { z } from 'zod';
import { escapeHtml, safeUrl } from '../../render/escape.js';
import type { DefModuleMountT } from '../../schema/split.js';
import { ImageValue, Slug } from '../../schema/types.js';
import { identityMigration, ModuleOpError, type FirstPartyModule, type ModuleOperation } from '../kernel.js';
import {
  EventItem,
  EventsConfig,
  EventsData,
  MAX_EVENTS,
  type EventsConfigT,
  type EventsDataT,
} from './schema.js';

// Operation-input ids reuse the canonical Slug (its length bound travels with
// the regex), matching commerce.catalog.
const id = Slug;
const eventTitle = z.string().min(1).max(160);
const eventDescription = z.string().max(600);

const addEventInput = EventItem;
const updateEventInput = z.object({
  id,
  // A null value CLEARS an optional field (time/description/image/cta); an
  // absent key leaves it unchanged. title/date are never clearable. The MERGED
  // event is re-validated against EventItem below, so no patch can leave an
  // event the schema forbids (e.g. a lone ctaLabel or an invalid date).
  fields: z.object({
    title: eventTitle.optional(),
    date: z.string().optional(),
    time: z.string().nullable().optional(),
    description: eventDescription.nullable().optional(),
    image: ImageValue.nullable().optional(),
    ctaLabel: z.string().min(1).max(60).nullable().optional(),
    ctaUrl: z.string().max(2000).nullable().optional(),
  }).strict().refine((fields) => Object.keys(fields).length > 0, 'at least one field is required'),
}).strict();
const removeEventInput = z.object({ id }).strict();
const reorderEventsInput = z.object({ orderedIds: z.array(id) }).strict();

type EventRecord = EventsDataT['events'][number];
type Locale = EventsConfigT['locale'];

const DEFAULT_CONFIG: EventsConfigT = { locale: 'cs' };

function invalidInput(error: z.ZodError): ModuleOpError {
  return new ModuleOpError('invalid-input', error.issues[0]?.message ?? 'invalid operation input');
}

function parseInput<T>(schema: z.ZodType<T>, input: unknown): T {
  const parsed = schema.safeParse(input);
  if (!parsed.success) throw invalidInput(parsed.error);
  return parsed.data;
}

function cloneData(data: EventsDataT): EventsDataT {
  return structuredClone(data);
}

function findEvent(data: EventsDataT, eventId: string): EventRecord {
  const event = data.events.find((candidate) => candidate.id === eventId);
  if (!event) throw new ModuleOpError('event-not-found', `event "${eventId}" was not found`);
  return event;
}

function reorderByIds<T extends { id: string }>(items: T[], orderedIds: string[], label: string): T[] {
  const byId = new Map(items.map((item) => [item.id, item]));
  const uniqueIds = new Set(orderedIds);
  const hasEveryItem = items.every((item) => uniqueIds.has(item.id));
  if (orderedIds.length !== items.length || uniqueIds.size !== orderedIds.length || !hasEveryItem) {
    throw new ModuleOpError('invalid-order', `${label} order must contain every current id exactly once`);
  }
  return orderedIds.map((orderedId) => byId.get(orderedId)!);
}

function operation<T>(
  schema: z.ZodType<T>,
  describe: string,
  transform: (data: EventsDataT, input: T) => void,
): ModuleOperation<EventsDataT> {
  return {
    input: schema,
    describe,
    apply(data, input) {
      const parsed = parseInput(schema, input);
      const result = cloneData(data);
      transform(result, parsed);
      return result;
    },
  };
}

const operations: Record<string, ModuleOperation<EventsDataT>> = {
  addEvent: operation(addEventInput, 'Add an event to the list.', (data, input) => {
    if (data.events.length >= MAX_EVENTS) {
      throw new ModuleOpError('too-many', `an events list holds at most ${MAX_EVENTS} events`);
    }
    if (data.events.some((event) => event.id === input.id)) {
      throw new ModuleOpError('duplicate-id', `event "${input.id}" already exists`);
    }
    data.events.push(input);
  }),
  updateEvent: operation(updateEventInput, 'Update fields on an event.', (data, input) => {
    const event = findEvent(data, input.id);
    const target = event as Record<string, unknown>;
    for (const [key, value] of Object.entries(input.fields)) {
      if (value === null) delete target[key];
      else target[key] = value;
    }
    // Re-validate the MERGED event so a patch can never leave it in a state the
    // schema forbids (invalid date, a lone ctaLabel/ctaUrl, over-long title).
    const revalidated = EventItem.safeParse(event);
    if (!revalidated.success) throw invalidInput(revalidated.error);
  }),
  removeEvent: operation(removeEventInput, 'Remove an event from the list.', (data, input) => {
    findEvent(data, input.id);
    data.events = data.events.filter((event) => event.id !== input.id);
  }),
  reorderEvents: operation(reorderEventsInput, 'Reorder all events in the list.', (data, input) => {
    data.events = reorderByIds(data.events, input.orderedIds, 'event');
  }),
};

/**
 * Format an ISO date for display via the runtime's Intl.DateTimeFormat (never a
 * date library). Pure and deterministic: the date is a plain calendar day, so
 * it is formatted in UTC — the same snapshot always yields the same string. A
 * malformed date (shouldn't reach here — the schema validates it) falls back to
 * the raw string rather than throwing from a render path.
 */
export function formatEventDate(date: string, locale: string = 'cs'): string {
  try {
    const [year, month, day] = date.split('-').map(Number);
    return new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' }).format(
      new Date(Date.UTC(year!, month! - 1, day!)),
    );
  } catch {
    return date;
  }
}

function sectionOpen(mount: DefModuleMountT, view: string): string {
  return `<section id="${escapeHtml(mount.anchor)}" class="ve-module ve-events ve-events--${escapeHtml(view)}">`;
}

function renderEventImage(image: EventRecord['image']): string {
  if (!image) return '';
  return `      <img class="ve-events__image" src="${safeUrl(image.src)}" alt="${escapeHtml(image.alt)}" loading="lazy">`;
}

function renderEventCta(event: EventRecord): string {
  if (event.ctaLabel === undefined || event.ctaUrl === undefined) return '';
  return `      <a class="ve-events__cta" href="${safeUrl(event.ctaUrl)}">${escapeHtml(event.ctaLabel)}</a>`;
}

function renderEvent(event: EventRecord, locale: Locale): string {
  const dateLine = event.time
    ? `${escapeHtml(formatEventDate(event.date, locale))} · ${escapeHtml(event.time)}`
    : escapeHtml(formatEventDate(event.date, locale));
  const lines = ['    <article class="ve-events__item">'];
  const image = renderEventImage(event.image);
  if (image) lines.push(image);
  lines.push(
    '      <div class="ve-events__body">',
    `        <p class="ve-events__date"><time datetime="${escapeHtml(event.date)}">${dateLine}</time></p>`,
    `        <h3 class="ve-events__title">${escapeHtml(event.title)}</h3>`,
  );
  if (event.description) lines.push(`        <p class="ve-events__desc">${escapeHtml(event.description)}</p>`);
  const cta = renderEventCta(event);
  if (cta) lines.push(cta);
  lines.push('      </div>', '    </article>');
  return lines.join('\n');
}

function renderEvents(
  mount: DefModuleMountT,
  data: EventsDataT,
  config: EventsConfigT | undefined,
  view: string,
): { html: string } {
  const parsedConfig = EventsConfig.parse(config ?? DEFAULT_CONFIG);
  const items = data.events.map((event) => renderEvent(event, parsedConfig.locale));
  const parts = [sectionOpen(mount, view)];
  if (parsedConfig.heading) parts.push(`  <h2 class="ve-events__heading">${escapeHtml(parsedConfig.heading)}</h2>`);
  parts.push(`  <div class="ve-events__grid ve-events__grid--${escapeHtml(view)}">`, ...items, '  </div>', '</section>');
  return { html: parts.join('\n') };
}

const migrate = identityMigration('content.events', EventsData);

export const contentEvents: FirstPartyModule<EventsDataT, EventsConfigT> = {
  id: 'content.events',
  contractVersion: 1,
  dataVersion: 1,
  dataSchema: EventsData,
  configSchema: EventsConfig,
  operations,
  views: {
    list: { render: (mount, data, config) => renderEvents(mount, data, config, 'list') },
    cards: { render: (mount, data, config) => renderEvents(mount, data, config, 'cards') },
  },
  migrate,
};
